import { useRef, useEffect, useCallback } from "react";

import { useTrackedRect } from "./useTrackedRect";
import { anchoredScrollLeft, clampPixelsPerSecond, maxScrollLeft } from "../utils/viewport";
import { clamp } from "@perceptron/utils/math";

type UseTimelineWheelProps = {
  timelineRefs: React.RefObject<HTMLDivElement | null>[];
  durationSeconds: number;
  pixelsPerSecond: number;
  setPixelsPerSecond: (value: number) => void;
  scrollLeft: number;
  setScrollLeft: (value: number | ((prev: number) => number)) => void;
  setScrollTop: (value: (prev: number) => number) => void;
  viewportWidth: number;
  maxScroll: number;
  maxScrollTop: number;
};

type WheelInput = {
  deltaX: number;
  deltaY: number;
  zoom: boolean;
  clientX: number;
};

const WHEEL_LINE_HEIGHT_PX = 16;

export const useTimelineWheel = ({
  timelineRefs,
  durationSeconds,
  pixelsPerSecond,
  setPixelsPerSecond,
  scrollLeft,
  setScrollLeft,
  setScrollTop,
  viewportWidth,
  maxScroll,
  maxScrollTop,
}: UseTimelineWheelProps) => {
  const wheelRequestAnimationFrameIdRef = useRef<number | null>(null);
  const pendingWheelEventsRef = useRef<WheelInput[]>([]);

  // Use the first ref (main timeline) for rect calculations
  const primaryTimelineRef = timelineRefs[0];
  const getTimelineEl = useCallback(() => primaryTimelineRef?.current ?? null, [primaryTimelineRef]);
  const timelineRectRef = useTrackedRect(getTimelineEl);

  // Apply batches in order so a modifier change cannot reinterpret earlier input.
  // Keep intermediate zoom/scroll values local until the entire frame is processed.
  const processWheelEvent = useCallback(() => {
    const events = pendingWheelEventsRef.current;
    pendingWheelEventsRef.current = [];
    wheelRequestAnimationFrameIdRef.current = null;
    if (!events.length) return;
    if (!timelineRectRef.current && primaryTimelineRef?.current) {
      timelineRectRef.current = primaryTimelineRef.current.getBoundingClientRect();
    }

    let nextPixelsPerSecond = pixelsPerSecond;
    let nextScrollLeft = scrollLeft;
    let scrollLimit = maxScroll;
    let verticalDelta = 0;
    for (const event of events) {
      if (!event.zoom) {
        nextScrollLeft = clamp(nextScrollLeft + event.deltaX, 0, scrollLimit);
        verticalDelta += event.deltaY;
        continue;
      }
      if (!timelineRectRef.current || durationSeconds <= 0) continue;
      const mouseX = event.clientX - timelineRectRef.current.left;
      const zoomedPixelsPerSecond = clampPixelsPerSecond(
        nextPixelsPerSecond * Math.pow(1.1, event.deltaY * -0.02),
        viewportWidth,
        durationSeconds,
      );
      nextScrollLeft = anchoredScrollLeft({
        scrollLeft: nextScrollLeft,
        fromPixelsPerSecond: nextPixelsPerSecond,
        toPixelsPerSecond: zoomedPixelsPerSecond,
        anchorX: mouseX,
        durationSeconds,
        viewportWidth,
      });
      nextPixelsPerSecond = zoomedPixelsPerSecond;
      scrollLimit = maxScrollLeft(durationSeconds, nextPixelsPerSecond, viewportWidth);
    }

    if (nextPixelsPerSecond !== pixelsPerSecond) setPixelsPerSecond(nextPixelsPerSecond);
    if (nextScrollLeft !== scrollLeft) setScrollLeft(nextScrollLeft);
    if (verticalDelta !== 0) setScrollTop((prev) => clamp(prev + verticalDelta, 0, maxScrollTop));
  }, [
    pixelsPerSecond,
    scrollLeft,
    maxScroll,
    maxScrollTop,
    durationSeconds,
    viewportWidth,
    primaryTimelineRef,
    timelineRectRef,
    setPixelsPerSecond,
    setScrollLeft,
    setScrollTop,
  ]);

  // Use a Ref to hold the latest process function.
  // This allows the DOM listener to be stable (never removed/re-added).
  const processWheelEventRef = useRef(processWheelEvent);
  useEffect(() => {
    processWheelEventRef.current = processWheelEvent;
  }, [processWheelEvent]);

  // ---------------------------------------------------------------------------
  // DOM Event Attachment
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();

      const rect = timelineRectRef.current;
      const scaleX = e.deltaMode === 1 ? WHEEL_LINE_HEIGHT_PX : e.deltaMode === 2 ? (rect?.width ?? 1) : 1;
      const scaleY = e.deltaMode === 1 ? WHEEL_LINE_HEIGHT_PX : e.deltaMode === 2 ? (rect?.height ?? 1) : 1;
      const input: WheelInput = {
        deltaX: e.deltaX * scaleX,
        deltaY: e.deltaY * scaleY,
        zoom: e.ctrlKey || e.metaKey,
        clientX: e.clientX,
      };
      const previous = pendingWheelEventsRef.current.at(-1);
      if (previous && previous.zoom === input.zoom && (!input.zoom || previous.clientX === input.clientX)) {
        previous.deltaX += input.deltaX;
        previous.deltaY += input.deltaY;
      } else {
        pendingWheelEventsRef.current.push(input);
      }

      if (wheelRequestAnimationFrameIdRef.current === null) {
        wheelRequestAnimationFrameIdRef.current = requestAnimationFrame(() => {
          processWheelEventRef.current();
        });
      }
    };

    // Attach listener to all refs
    const elements = timelineRefs.map((ref) => ref.current).filter((el): el is HTMLDivElement => el !== null);

    elements.forEach((element) => {
      element.addEventListener("wheel", onWheel, { passive: false });
    });

    return () => {
      elements.forEach((element) => {
        element.removeEventListener("wheel", onWheel);
      });
      if (wheelRequestAnimationFrameIdRef.current !== null) {
        cancelAnimationFrame(wheelRequestAnimationFrameIdRef.current);
        wheelRequestAnimationFrameIdRef.current = null;
      }
      pendingWheelEventsRef.current = [];
    };
  }, [timelineRefs, timelineRectRef]);

  return {};
};
